import { useTellescopeQuery, useUsersMap } from "@tellescope/tanstack-query";
import { AvatarGroup, AvatarItem, Button, cn } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";
import { formatDateTime, formatInitials, formatName } from "@tellescope/utils";
import { CalendarOff } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useIsUserOutOfOfficeNow } from "../useIsUserOutOfOfficeNow";
import { EditTeamChannelDialog } from "./EditTeamChannelDialog";

export interface TeamChatHeaderProps {
  chatroomId: string;
}

export const TeamChatHeader = ({ chatroomId }: TeamChatHeaderProps) => {
  const { t } = useTranslation("team-chat");
  const chatroomQuery = useTellescopeQuery({
    model: "chat_rooms",
    method: "getOne",
    request: chatroomId,
    enabled: !!chatroomId,
  });

  const usersMap = useUsersMap();
  const isUserOutOfOffice = useIsUserOutOfOfficeNow();
  const avatarGroupItems = useMemo<AvatarItem[]>(
    () =>
      chatroomQuery.data?.userIds?.reduce<AvatarItem[]>((acc, userId) => {
        const user = usersMap[userId];

        if (!user) return acc;

        acc.push({
          id: userId,
          name: formatName(user),
          initials: formatInitials(user),
          avatar: user.avatar,
          useStaticBackground: true,
        });

        return acc;
      }, []) ?? [],
    [chatroomQuery.data?.userIds, usersMap]
  );

  const outOfOfficeNames = useMemo(
    () =>
      chatroomQuery.data?.userIds?.reduce<string[]>((acc, userId) => {
        const user = usersMap[userId];
        if (user && isUserOutOfOffice(user)) acc.push(formatName(user));
        return acc;
      }, []) ?? [],
    [chatroomQuery.data?.userIds, usersMap, isUserOutOfOffice]
  );

  return (
    <div className="p-4 border-b border-b-gray-200 space-y-2">
      <div className="w-full flex justify-between items-start">
        <EditTeamChannelDialog chatroomId={chatroomId} autoFocusField="userIds">
          <button className="hover:opacity-70 transition-opacity flex items-center gap-1.5">
            <AvatarGroup items={avatarGroupItems} size="sm" />
            {outOfOfficeNames.length > 0 && (
              <Tooltip>
                <Tooltip.Trigger asChild>
                  <CalendarOff className="h-4 w-4 text-amber-600" />
                </Tooltip.Trigger>
                <Tooltip.Content>
                  {t("Out of office: {{names}}", { names: outOfOfficeNames.join(", ") })}
                </Tooltip.Content>
              </Tooltip>
            )}
          </button>
        </EditTeamChannelDialog>
        {!!chatroomQuery.data && (
          <p className="text-xs text-muted-foreground">
            {formatDateTime(chatroomQuery.data?.updatedAt, "MMM D, YYYY at hh:mm a", {
              unit: "days",
              amount: 3,
              direction: "both",
            })}
          </p>
        )}
      </div>
      <EditTeamChannelDialog chatroomId={chatroomId} autoFocusField="description">
        <Button
          variant="link"
          className={cn(
            "text-xs py-0.5 px-0 h-auto max-w-[20%] truncate inline line-clamp-2 text-left text-wrap hover:underline",
            chatroomQuery.data?.description ? undefined : "italic text-gray-400"
          )}
        >
          {chatroomQuery.data?.description || t("No description")}
        </Button>
      </EditTeamChannelDialog>
    </div>
  );
};
